import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { HAFamilyHistoryOptionDTO, HAFamilyHistoryOptionUI } from 'src/app/modules/shared/types';
import { LookupService } from 'src/app/services/ha-new/lookup.service';
import { MedicalHistoryService } from 'src/app/services/ha-new/medical-history.service';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';

@Component({
	selector: 'app-family-history',
	templateUrl: './family-history.component.html',
	styleUrls: ['./family-history.component.scss'],
})
export class FamilyHistoryComponent implements OnInit {
	relationOptions: HAFamilyHistoryOptionUI[] = [];
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** holds appointment id */
	apptID: number;
	/** holds readOnly flag */
	readOnly: boolean = false;
	/** holds ha id */
	healthAssessmentID: number;
	/** holds past present medical conditions */
	diagnosisOptions: HAFamilyHistoryOptionUI[] = [];
	relationsList: HAFamilyHistoryOptionDTO[] = [];
	/** holds scroll values */
	scrollTo = '';
	returningUser: boolean = false;

	constructor(
		private lookupService: LookupService,
		private medicalHistoryService: MedicalHistoryService,
		private spinner: SpinnerComponent,
		private activatedRoute: ActivatedRoute,
		private haService: NewHAService
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			this.apptID = params.apptID;
			this.readOnly = params.readOnly === 'true';
			this.returningUser = params.returningUser === 'true';
			if (this.scrollTo != 'false') this.scrollTo = params.section;
			this.healthAssessmentID = params.healthAssessmentID;
		});
	}

	ngOnInit() {
		this.getValues();
	}

	getValues() {
		this.spinner.startLoader();
		forkJoin({
			relationOptions: this.lookupService.relativeOptionsLookup(),
			diagnosisOptions: this.lookupService.diagnosisOptionsLookup(),
			getFamilyHistory: this.medicalHistoryService.getFamilyHistory(this.apptID, this.healthAssessmentID),
		}).subscribe(
			res => {
				this.relationOptions = res.relationOptions.data;
				this.diagnosisOptions = res.diagnosisOptions.data;
				this.relationOptions.forEach(option => {
					option['checked'] = false;
					option['diagnosis'] = new FormControl([]);
				});
				this.setFamilyHistory(res.getFamilyHistory.data);

				this.spinner.stopLoader();

				if (this.scrollTo != 'false') {
					if (this.haService.scrollView(this.scrollTo)) {
						this.scrollTo = 'false';
					}
				}
			},
			err => {
				this.spinner.stopLoader();
				console.error(`[${FamilyHistoryComponent.name}][${this.getValues.name}]`, 'Error occurred while saving past present medical conditions ', err);
			}
		);
	}

	/**
	 * set family history data
	 */
	setFamilyHistory(data) {
		if (data && data.relativeResponses) {
			this.relationsList = data.relativeResponses;
			data.relativeResponses.forEach(element => {
				const index = this.relationOptions.findIndex(option => option.id === element.optionID);
				this.relationOptions[index].checked = true;
				if (element.diagnosis) {
					const diagnosisArray = element.diagnosis.filter(option => option.optionID > 0).map(option => option.optionID);
					this.relationOptions[index]['diagnosis'].setValue(diagnosisArray);
				}
			});
		}
	}

	/**
	 * on selecting relation
	 * @param event
	 * @param relation
	 */
	relationSelection(event: MatCheckboxChange, relation: HAFamilyHistoryOptionUI) {
		relation.checked = event.checked;
		// Pushing into array if checked
		if (event.checked) {
			this.relationsList.push({ optionID: relation.id });
			this.conditionSelection(new MatSelectChange(undefined, relation.diagnosis.value), relation);
		} else {
			const index = this.relationsList.findIndex(element => element.optionID === relation.id);
			this.relationsList.splice(index, 1);
			relation['showErrorMessage'] = false;
		}
	}

	/**
	 * on selecting medical condition
	 * @param event
	 * @param relation
	 */
	conditionSelection(event: MatSelectChange, relation: HAFamilyHistoryOptionUI) {
		if (event.value) {
			const conditionsList = [];
			event.value.forEach(element => {
				conditionsList.push({ optionID: element });
			});
			const index = this.relationsList.findIndex(element => element.optionID === relation.id);
			this.relationsList[index]['diagnosis'] = conditionsList;
			relation['diagnosis'].setValue(event.value);
			relation['showErrorMessage'] = false;
		}
	}

	/**
	 * save data on clicking next
	 */
	next() {
		if (!this.readOnly) {
			let save = true;
			if (this.relationsList) {
				this.relationsList.forEach(element => {
					const index = this.relationOptions.findIndex(relation => relation.id === element.optionID);
					if (!element.diagnosis || element.diagnosis.length === 0) {
						this.relationOptions[index]['showErrorMessage'] = true;
						save = false;
					} else {
						this.relationOptions[index]['showErrorMessage'] = false;
					}
				});
			}
			if (save) {
				this.saveFamilyHistory();
			}
		}
	}

	/**
	 * saves past and present medical condition values
	 */
	saveFamilyHistory() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.medicalHistoryService
				.saveFamilyHistory(this.apptID, this.healthAssessmentID, {
					healthAssessmentID: this.healthAssessmentID,
					relativeResponses: this.relationsList,
				})
				.subscribe(
					res => {
						this.haService.redirect(
							[],
							[],
							['health-goals'],
							this.apptID,
							this.healthAssessmentID,
							this.readOnly,
							'lifestyle-health-goals',
							this.returningUser
						);
						this.spinner.stopLoader();
						console.log(res);
					},
					err => {
						this.spinner.stopLoader();
						console.error(`[${FamilyHistoryComponent.name}][${this.saveFamilyHistory.name}]`, 'Error occurred while saving family history response ', err);
					}
				)
		);
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
